// Cross-references an SVG diagram's own text against the Dictionary glossary (dictionaryUtils.js)
// — e.g. a Visio export with a shape labeled "Managed Configuration" gets a "Related terms" tag
// linking straight to that definition. Whole-word/whole-phrase match, not just whole-label
// equality — labels are often compound ("Common Data Environment (Projectwise)", "Quality
// Assurance & Quality Control"), so a term needs to be found *within* a label, not just equal to
// one. Still no fuzzy/abbreviation matching ("QA & QC" won't match "Quality Assurance"/"Quality
// Control" just because they're related, and a naive plain-substring check would even wrongly
// match "Managed Configuration" inside "**Un**managed Configuration" — a real false positive
// found in this project's own data) — a wrong "related term" undermines trust in something meant
// to back a BEP submission more than a missed one does.

function decodeXmlEntities(s) {
  return s
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'");
}

// Reads whatever human-readable labels an SVG carries: <desc> (Visio's per-shape description —
// usually the cleanest single label, e.g. "Managed Configuration") and <text>/nested <tspan>
// content (visible on-diagram labels — tags stripped, so multi-line text collapses to one
// string; fine for exact-match purposes, not meant to reconstruct the diagram's real layout).
export function extractSvgLabels(svgText) {
  const labels = new Set();
  for (const m of (svgText || "").matchAll(/<desc>([^<]*)<\/desc>/g)) {
    const t = decodeXmlEntities(m[1]).trim();
    if (t) labels.add(t);
  }
  for (const m of (svgText || "").matchAll(/<text\b[^>]*>([\s\S]*?)<\/text>/g)) {
    const t = decodeXmlEntities(m[1].replace(/<[^>]*>/g, "")).trim();
    if (t) labels.add(t);
  }
  return [...labels];
}

// Whole-word/whole-phrase containment check — \b anchors on both ends of the (regex-escaped)
// term so "Managed Configuration" matches inside "Common Data Environment... Managed
// Configuration" but NOT inside "Unmanaged Configuration" (the character right before "Managed"
// there is "n", a word character, so \b correctly refuses the match — plain .includes() would
// have gotten this specific case wrong).
function containsTerm(haystack, term) {
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${escaped}\\b`, "i").test(haystack);
}

// dictionaryEntries: [{term, definition}] from parseDictionary() — returns the subset where the
// term appears as a whole word/phrase in at least one extracted label.
export function matchDictionaryTerms(labels, dictionaryEntries) {
  return (dictionaryEntries || []).filter((e) => labels.some((label) => containsTerm(label, e.term)));
}
